import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import Model from "@tripian/model";
import { api } from "@tripian/core";
import { saveNotification } from "../redux/action/user";
import useTranslate from "./useTranslate";

const useFeedback = () => {
  const [loadingFeedback, setLoadingFeedback] = useState<boolean>(true);
  const [feedbacks, setFeedbacks] = useState<Model.Feedbacks>();
  const [userFeedbacksList, setUserFeedbacksList] = useState<
    Model.UserFeedback[]
  >([]);
  const [loadingUserFeedbacks, setLoadingUserFeedbacks] =
    useState<boolean>(false);
  const hasInitialized = useRef<boolean>(false);

  const { t } = useTranslate();
  const dispatch = useDispatch();

  // Get user feedbacks with optional force refresh
  const userFeedbacks = useCallback(
    async (forceOnce: boolean = false): Promise<Model.UserFeedback[]> => {
      if (userFeedbacksList.length > 0 && !forceOnce) {
        return userFeedbacksList;
      }

      setLoadingUserFeedbacks(true);
      try {
        const fetchedUserFeedbacks = await api.userFeedbacks();
        setUserFeedbacksList(fetchedUserFeedbacks);
        return fetchedUserFeedbacks;
      } catch (error) {
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.ERROR,
            "feedback",
            error as string
          )
        );
        throw error;
      } finally {
        setLoadingUserFeedbacks(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  // Send a new feedback
  const feedbackSend = useCallback(
    async (feedbackRequest: Model.FeedbackRequest) => {
      try {
        await api.feedbackSend(feedbackRequest);
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.SUCCESS,
            "feedback",
            t("notification.feedbackSuccess.message")
          )
        );
      } catch (error) {
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.ERROR,
            "feedback",
            t("notification.feedbackError.message")
          )
        );
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Reply to an existing feedback
  const feedbackReply = useCallback(
    async (
      feedbackRequest: Model.FeedbackReplyRequest
    ): Promise<Model.UserFeedback[]> => {
      try {
        await api.feedbackReply(feedbackRequest);
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.SUCCESS,
            "feedback",
            t("notification.replySuccess.message")
          )
        );
        // Refresh the user feedbacks list after replying
        return await userFeedbacks(true);
      } catch (error) {
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.ERROR,
            "feedback",
            t("notification.replyError.message")
          )
        );
        throw error;
      }
    },
    [dispatch, t, userFeedbacks]
  );

  // Update feedback read status
  const feedbackUpdate = useCallback(
    async (
      feedbackId: string,
      isRead: boolean
    ): Promise<Model.UserFeedback[]> => {
      try {
        // Call the API to update the feedback read status - pass feedbackId directly
        await api.feedbackUpdate(feedbackId, isRead);

        // Update the local state without making a new API call
        setUserFeedbacksList(prev =>
          prev.map(feedback =>
            feedback.id === feedbackId
              ? { ...feedback, is_read: isRead }
              : feedback
          )
        );

        return userFeedbacksList.map(feedback =>
          feedback.id === feedbackId
            ? { ...feedback, is_read: isRead }
            : feedback
        );
      } catch (error) {
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.ERROR,
            "feedback",
            "Failed to update feedback status"
          )
        );
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  useEffect(() => {
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    const loadFeedbacks = async () => {
      setLoadingFeedback(true);
      try {
        const fetchedFeedbacks = await api.feedbacks();
        setFeedbacks(fetchedFeedbacks);
      } catch (feedBacksError) {
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.ERROR,
            "feedback",
            feedBacksError as string
          )
        );
      } finally {
        setLoadingFeedback(false);
      }
    };

    loadFeedbacks();
  }, [dispatch]);

  return {
    loadingFeedback,
    feedbacks,
    userFeedbacks,
    feedbackSend,
    feedbackReply,
    feedbackUpdate,
    loadingUserFeedbacks,
  };
};

export default useFeedback;
